/**
 * Résout l'utilisateur Clerk authentifié → row `public.clients` Supabase.
 * Match : Clerk email == clients.contact_email (lowercase).
 *
 * Source de vérité business :
 *   - clients.metadata.pack       → 'solo' | 'pro'
 *   - clients.metadata.api_usage_month → entier (rempli par backend)
 *   - clients.started_at          → calcul engagement
 *   - clients.metadata.voice_profile_status → 'empty' | 'in_progress' | 'complete'
 *
 * En mode démo (VITE_DEMO_MODE=true), retourne un tenant mocké pour permettre
 * à Sebastian de tester l'UI sans Clerk ni Supabase configurés.
 */
import { useQuery } from '@tanstack/react-query';
import { useUser, useAuth, IS_DEMO_MODE } from '@/lib/auth';
import { supabasePublic } from '@/lib/supabase';
import { packFromString, type Pack } from '@/lib/pack';

export type VoiceProfileStatus = 'empty' | 'in_progress' | 'complete';

export type Tenant = {
  id: string;
  slug: string | null;
  company_name: string;
  contact_name: string | null;
  contact_email: string | null;
  pack: Pack;
  api_usage_month: number;
  voice_profile_status: VoiceProfileStatus;
  /** Liste agent IDs activés. Si vide, on retombe sur les défauts du pack. */
  active_agents: string[];
  started_at: string | null;
  metadata: Record<string, unknown>;
};

const DEMO_TENANT: Tenant = {
  id: '44444444-4444-4444-4444-444444444444',
  slug: 'marie-dupont',
  company_name: 'Marie Dupont Coaching',
  contact_name: 'Marie Dupont',
  contact_email: 'marie@example.com',
  pack: 'solo',
  api_usage_month: 1820,
  voice_profile_status: 'in_progress',
  active_agents: ['A1', 'A2', 'A3'],
  started_at: '2026-04-20',
  metadata: {},
};

function pickVoiceStatus(value: unknown): VoiceProfileStatus {
  if (value === 'complete' || value === 'in_progress' || value === 'empty') return value;
  return 'empty';
}

function pickStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

export function useTenant() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const email = user?.primaryEmailAddress?.emailAddress?.toLowerCase() ?? null;

  return useQuery<Tenant>({
    queryKey: ['tenant', email],
    enabled: isLoaded && (IS_DEMO_MODE || (isSignedIn && !!email)),
    queryFn: async () => {
      if (IS_DEMO_MODE) return DEMO_TENANT;
      if (!email) throw new Error('No email on Clerk user');

      const supa = supabasePublic(() => getToken());
      const { data, error } = await supa
        .from('clients')
        .select('id, slug, company_name, contact_name, contact_email, started_at, metadata')
        .eq('contact_email', email)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        throw new Error(
          `Aucun compte Ducens trouvé pour ${email}. Contacte sebastian@ducens.io.`
        );
      }

      const meta = (data.metadata ?? {}) as Record<string, unknown>;
      return {
        id: data.id,
        slug: data.slug ?? null,
        company_name: data.company_name,
        contact_name: data.contact_name,
        contact_email: data.contact_email,
        pack: packFromString(meta['pack']),
        api_usage_month:
          typeof meta['api_usage_month'] === 'number' ? meta['api_usage_month'] : 0,
        voice_profile_status: pickVoiceStatus(meta['voice_profile_status']),
        active_agents: pickStringArray(meta['active_agents']),
        started_at: data.started_at,
        metadata: meta,
      };
    },
  });
}
